import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import {
  EventBusEvents,
  jobEventBus,
  JobCompletedPayload,
} from '@lib/utils/eventBus';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import FeatureLockBanner from '@lib/components/FeatureLockBanner';
import useDocumentationHelpPopupProps from '@lib/utils/documentation/useDocumentationHelpPopupProps';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import {
  isForbiddenError,
  isUpgradePlanRequiredError,
} from '@lib/utils/graphQLErrors';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { IconName } from '@lib/components/Modal/enums';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Trans from '@lib/components/Trans/Trans';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useModalManagement from '@lib/hooks/useModalManagement';
import useSearchParam from '@lib/hooks/useSearchParam';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import { Modals } from '@lib/enums/modals';
import { PlanState } from '@lib/enums/plan';
import { Value, Values } from '@lib/interfaces/form';
import { DOCUMENT_LIST_FILTER_FORM } from '@lib/enums/localStorageKeys';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Attachment,
  Documentation,
  BackgroundJobKindEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import useDocumentDetectionRefetch from 'routes/Documentation/utils/useDocumentDetectionRefetch';
import isDocumentDetecting from 'routes/Documentation/utils/isDocumentDetecting';
import DocumentListFilter from './DocumentListFilter/DocumentListFilter';
import DocumentBulkEditModal from './DocumentBulkEditModal/DocumentBulkEditModal';
import DocumentDeleteLinkedWarning from './components/DocumentDeleteLinkedWarning';
import { DOCUMENT_LIST_FILTER_EMPTY_VALUES } from './utils/useDocumentFilterFields';
import useDocumentListActions from './utils/useDocumentListActions';

const modals = [Modals.DocumentListFilter];

interface DocumentListProps {
  isApprovals?: boolean;
}

function DocumentList({ isApprovals }: DocumentListProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const documentationHelpPopup = useDocumentationHelpPopupProps();

  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  // Cache-derived: `whoami` can lag a plan change made in another session
  // (e.g. SystemAdmin disabling the feature mid-session), so this can still
  // read `false` for a moment after the company plan was actually locked.
  const isAccessLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.Read;
  // AI field-detection state (aiDetectionStatus / suggestedCodes) is gated on the
  // BE by DOCUMENTATION_GATE (documentation_management + ai_assistance). Requesting
  // it without both nullifies the whole resolver, so only select those fields when
  // the company has AiAssistance (Write), documentation isn't plan-locked, AND the
  // current user/team isn't individually restricted from AiAssistance.
  const isAiDetectionEnabled =
    getUserAccessByCompany(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    getUserAccessByComponent(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    !isAccessLocked;

  const [isFavoritedMode, setIsFavoritedMode] = useQueryParam(
    'favorites',
    withDefault(BooleanParam, false),
  );
  const [searchQuery] = useSearchParam();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(
    DOCUMENT_LIST_FILTER_FORM,
    DOCUMENT_LIST_FILTER_EMPTY_VALUES as { [key: string]: Value },
  );

  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, openModal, closeModal } = useModalManagementResult;
  const useBulkSelectResult = useTableItemsBulkSelect<Documentation>();

  const {
    bulkEditIds,
    closeBulkEditModal,
    closeDeleteModal,
    closeFileViewer,
    closeVersionNotes,
    collection,
    deleteIds,
    error: documentationsError,
    fileViewerDocument,
    filterCounter,
    filterFields,
    firstLoading,
    isBulkEditModalOpen,
    isDeleteModalOpen,
    isEmptyState,
    loading,
    onConfirmedDelete,
    onToggleFavoritedMode,
    refetch,
    tableActionsForSelected,
    tableActionsMenu,
    tableColumns,
    totalCount,
    versionNotesDocument,
  } = useDocumentListActions({
    filterParams,
    isAiDetectionEnabled,
    isApprovals,
    isFavoritedMode,
    isViewLocked: isAccessLocked,
    isViewOnly,
    setIsFavoritedMode,
    useBulkSelectResult,
  });

  // A FORBIDDEN_ERROR (or UPGRADE_PLAN_REQUIRED) on this query means the
  // access check above raced a plan change and read stale data — treat it as
  // locked instead of falling through to the generic error page below.
  const isViewLocked =
    isAccessLocked ||
    isForbiddenError(documentationsError) ||
    isUpgradePlanRequiredError(documentationsError);
  // Locked affordances (banner, import, filter chips) only make sense for
  // Full Admins, who can trigger the plan-upgrade modal. For the User role,
  // hide them entirely so they aren't shown a dead-end CTA.
  const showLockedAffordances = isViewLocked && isFullAdmin;

  const onUpgrade = () =>
    handleOpenPlanModal(PlanState.Locked, [
      CompanyPlanUpgradeFeatureEnum.Documentation,
    ]);

  const onBulkEditCompleted = () => {
    closeBulkEditModal();
    useBulkSelectResult.handleUnselectAllTableItems();
    refetch();
  };

  // The import root's own job completes as soon as its files are created —
  // well before its DOCUMENTATION_FIELD_DETECTION child (detection) settles.
  // This first refetch just catches up the newly-imported rows; it can't see
  // detection finishing later, since children aren't part of the "my jobs"
  // completion feed below. useDocumentDetectionRefetch (same hook the
  // single-document screens use) covers that by watching the raw
  // backgroundJobProgress stream directly, so the list doesn't get stuck
  // showing "Detecting…" until a manual reload. A cancelled import re-pulls the
  // now-CANCELLED field statuses the same way.
  const isDetecting =
    isAiDetectionEnabled && collection.some((doc) => isDocumentDetecting(doc));
  useDocumentDetectionRefetch({ isDetecting, refetch });

  useEffect(() => {
    const handler = ({ kind }: JobCompletedPayload) => {
      if (kind === BackgroundJobKindEnum.DocumentationImport) {
        refetch();
      }
    };
    jobEventBus.on(EventBusEvents.JobCompleted, handler);
    return () => {
      jobEventBus.off(EventBusEvents.JobCompleted, handler);
    };
  }, [refetch]);

  if (documentationsError && !isViewLocked) {
    return (
      <ErrorPage
        status={getStatusFromError(documentationsError)}
        description={getDescriptionFromError(documentationsError)}
      />
    );
  }

  const openFilter = () => openModal(Modals.DocumentListFilter);
  const closeFilter = () => closeModal(Modals.DocumentListFilter);
  const onFilterSubmit = (values: Values) => {
    setFilterParams(values as { [key: string]: Value });
    closeFilter();
  };
  const clearFilter = () =>
    setFilterParams(
      DOCUMENT_LIST_FILTER_EMPTY_VALUES as { [key: string]: Value },
    );
  const isFilterModalOpen = isModalOpened(Modals.DocumentListFilter);

  const emptyState = isFavoritedMode ? (
    <ListEmptyState
      icon={IconName.BookmarkStar}
      isFilterApplied={filterCounter > 0}
      onResetFilter={clearFilter}
      title={t('no-favorite-documents-yet')}
      text={t('no-favorite-documents-text')}
      actions={[
        <Button
          key="browse"
          buttonText={t('browse-all-documents')}
          buttonType={ButtonTypes.primaryFilled}
          onClick={() => setIsFavoritedMode(false)}
        />,
      ]}
    />
  ) : (
    <ListEmptyState
      isFilterApplied={filterCounter > 0}
      onResetFilter={clearFilter}
      title={t('no-documents-yet')}
      text={<Trans i18nKey="no-documents-text" />}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="create"
                buttonText={t('create-document')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={() =>
                  navigate(APP_URLS.dashboard.documentation.add.path)
                }
              />,
            ]
      }
    />
  );

  const headerActions = (() => {
    if (isViewOnly) return <ViewOnlyChip />;
    // For non-Full-Admin Users on a locked plan, drop the Import button
    // entirely — they can't unlock, so the locked-state CTA is noise.
    const showImportButton = !isViewLocked || isFullAdmin;
    const importButton = showImportButton ? (
      <Button
        buttonText={t('import')}
        buttonType={
          isViewLocked
            ? ButtonTypes.secondaryOutlined
            : ButtonTypes.primaryOutlined
        }
        leftIcon={isViewLocked ? undefined : 'input'}
        rightIcon={isViewLocked ? 'lock' : undefined}
        onClick={
          isViewLocked
            ? onUpgrade
            : () => navigate(APP_URLS.dashboard.documentation.import.path)
        }
      />
    ) : null;
    return (
      <>
        {importButton && isViewLocked ? (
          <Tooltip body={t('unlock-import-tooltip')}>{importButton}</Tooltip>
        ) : (
          importButton
        )}
        <Button
          buttonText={t('create-document')}
          leftIcon="add"
          onClick={() => navigate(APP_URLS.dashboard.documentation.add.path)}
        />
      </>
    );
  })();

  const renderBody = () => {
    if (firstLoading) return <CircleLoader />;
    const isEmptyWithoutFilter = isEmptyState && filterCounter === 0;
    return (
      <>
        <DocumentListFilter
          favoritesDisabled={!isFavoritedMode && isEmptyWithoutFilter}
          filterCounter={filterCounter}
          filterDisabled={isEmptyWithoutFilter}
          filterFields={filterFields}
          filterParams={filterParams}
          isFavoritedMode={isFavoritedMode}
          isFilterModalOpen={isFilterModalOpen}
          isFullAdmin={isFullAdmin}
          isLocked={isViewLocked}
          onUpgrade={onUpgrade}
          openFilter={openFilter}
          closeFilter={closeFilter}
          onFilterSubmit={onFilterSubmit}
          onToggleFavoritedMode={onToggleFavoritedMode}
          clearFilter={clearFilter}
          isApprovals={isApprovals}
        />
        {(!!collection?.length || searchQuery) && (
          <Search
            placeholder={t(
              showLockedAffordances
                ? 'documents-search-placeholder-base'
                : 'documents-search-placeholder-premium',
            )}
            useSearchParam
            usePagination
          />
        )}
        <Table
          actionsForSelected={tableActionsForSelected}
          actionsMenu={tableActionsMenu}
          columns={tableColumns}
          counterText={t('document-plural', { count: totalCount })}
          data={collection}
          emptyState={emptyState}
          id="documents"
          loading={loading}
          totalEntries={totalCount}
          useBulkSelectResult={useBulkSelectResult}
        />
      </>
    );
  };

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageBody withoutHeader>
        {showLockedAffordances && !isApprovals && (
          <FeatureLockBanner
            bannerId="documentation-advanced-features"
            title={t('advanced-document-features-locked')}
            description={<Trans i18nKey="enable-access-control-text" />}
            helpPopup={documentationHelpPopup}
            onUpgrade={onUpgrade}
          />
        )}
        <DashboardTitleHeader
          title={t(isApprovals ? 'approvals' : 'documents')}
          actions={headerActions}
        />
        {renderBody()}
      </PageBody>
      {!isViewLocked && (
        <DocumentBulkEditModal
          ids={bulkEditIds}
          isOpen={isBulkEditModalOpen}
          onClose={closeBulkEditModal}
          onCompleted={onBulkEditCompleted}
        />
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="documents-bulk-delete-text" />}
        icon={IconName.Delete}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      >
        <DocumentDeleteLinkedWarning
          ids={deleteIds}
          isOpen={isDeleteModalOpen}
          isViewLocked={isViewLocked}
        />
      </ConfirmationModal>
      <FileViewerModal
        attachment={
          _get(fileViewerDocument, [
            'currentVersion',
            'attachmentAttached',
          ]) as Attachment
        }
        isOpen={!!fileViewerDocument}
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        onClose={closeFileViewer}
        useDownloadTool={false}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={closeVersionNotes}
      />
    </>
  );
}

export default DocumentList;
